/**
 * Wünsche pro Praktikumsstelle, für Schulen, die Plätze nach Eingang vergeben.
 *
 * Je Stelle und Fachbereich ein Block mit den Schülern, die sie sich gewünscht haben,
 * in der Reihenfolge des Eintragens. Den Rang rechnet das Backend
 * (services/wish-report.ts) über alle Schüler der Schule. Die Filter hier blenden nur
 * Zeilen aus und lassen den Rang stehen: "Rang 3" heißt auch in einer gefilterten
 * Liste "der dritte Wunsch an dieser Stelle insgesamt".
 */

import { api } from "../api";
import type { WishReport, WishReportBlock, WishReportEntry } from "../types";
import { initDropdownCheck, DropdownCheckHandle } from "../components/dropdown-check";
import { showToast } from "../utils/toast";
import { openModal } from "../components/modal";
import { Icons } from "../utils/icons";
import { fitOnResize } from "../utils/fit-height";
import { formatDateTime } from "../utils/date-utils";
import { exportWuensche } from "../utils/export-service";
import { esc } from "../utils/html";

/** Filterwert für Schüler ohne Klasse. */
const OHNE_KLASSE = "ohne";

let report: WishReport | null = null;
/** Leer bedeutet: kein Filter. */
let selectedDepts: string[] = [];
let selectedClasses: string[] = [];
let deptDropdown: DropdownCheckHandle | null = null;
let classDropdown: DropdownCheckHandle | null = null;

function classKey(e: WishReportEntry): string {
  return e.classId === null ? OHNE_KLASSE : String(e.classId);
}

// ── Filter ──

function visibleBlocks(r: WishReport): WishReportBlock[] {
  const depts = new Set(selectedDepts);
  const classes = new Set(selectedClasses);
  return r.stellen
    .filter(b => depts.size === 0 || depts.has(b.departmentId))
    .map(b => ({ ...b, eintraege: b.eintraege.filter(e => classes.size === 0 || classes.has(classKey(e))) }))
    .filter(b => b.eintraege.length > 0);
}

/** Die Auswahllisten entstehen aus den Daten: angeboten wird nur, wozu es Wünsche gibt. */
function fillFilters(el: HTMLElement, r: WishReport): void {
  const depts = new Map<string, string>();
  const classes = new Map<string, string>();
  for (const b of r.stellen) {
    depts.set(b.departmentId, b.departmentName);
    for (const e of b.eintraege) classes.set(classKey(e), e.className ?? "Ohne Klasse");
  }
  const byName = (a: [string, string], b: [string, string]) =>
    a[0] === OHNE_KLASSE ? 1 : b[0] === OHNE_KLASSE ? -1 : a[1].localeCompare(b[1], "de");

  // Eine Auswahl, zu der es nach dem Neuladen nichts mehr gibt, fällt weg.
  selectedDepts = selectedDepts.filter(v => depts.has(v));
  selectedClasses = selectedClasses.filter(v => classes.has(v));

  const item = ([value, label]: [string, string]) =>
    `<label class="dropdown-check-item"><input type="checkbox" value="${esc(value)}" /> ${esc(label)}</label>`;
  el.querySelector<HTMLElement>("#ww-dept .dropdown-check-panel")!.innerHTML =
    [...depts].sort(byName).map(item).join("");
  el.querySelector<HTMLElement>("#ww-class .dropdown-check-panel")!.innerHTML =
    [...classes].sort(byName).map(item).join("");
  deptDropdown?.setValues(selectedDepts);
  classDropdown?.setValues(selectedClasses);
}

/** Aktive Filter als Text für den PDF-Kopf. */
function filterText(r: WishReport): string {
  const parts: string[] = [];
  if (selectedDepts.length > 0) {
    const names = new Map(r.stellen.map(b => [b.departmentId, b.departmentName]));
    parts.push(`Fachbereich: ${selectedDepts.map(d => names.get(d) ?? d).join(", ")}`);
  }
  if (selectedClasses.length > 0) {
    const names = new Map<string, string>();
    for (const b of r.stellen) for (const e of b.eintraege) names.set(classKey(e), e.className ?? "Ohne Klasse");
    parts.push(`Klasse: ${selectedClasses.map(c => names.get(c) ?? c).join(", ")}`);
  }
  return parts.join("   ·   ");
}

// ── Darstellung ──

const INEXACT_TITLE = "Vor der sekundengenauen Erfassung eingetragen. Angezeigt wird das letzte Speichern der Wunschliste, der Wunsch kann früher entstanden sein.";

function renderRow(e: WishReportEntry): string {
  return `
    <tr>
      <td class="ww-rank">${e.rank}</td>
      <td>${esc(e.studentName)}</td>
      <td>${esc(e.className ?? "")}</td>
      <td>Nr. ${e.priority}</td>
      <td class="ww-time">${formatDateTime(e.createdAt)}${
        e.exact ? "" : `<span class="badge badge-amber" title="${esc(INEXACT_TITLE)}">ungenau</span>`}</td>
    </tr>`;
}

function renderBlock(b: WishReportBlock): string {
  const count = b.eintraege.length === 1 ? "1 Wunsch" : `${b.eintraege.length} Wünsche`;
  return `
    <section class="ww-block">
      <div class="ww-head">
        <span class="ww-dot" style="background:${esc(b.departmentColor)}"></span>
        <strong>${esc(b.practiceName)}</strong>
        <span>·</span>
        <span>${esc(b.departmentName)}</span>
        ${b.ort ? `<span class="ww-ort">${esc(b.ort)}</span>` : ""}
        <span class="ww-count">${count}</span>
      </div>
      <table class="ww-table">
        <thead><tr>
          <th class="ww-rank">Rang</th><th>Schüler</th><th>Klasse</th><th>Wunsch</th><th>Eingetragen am</th>
        </tr></thead>
        <tbody>${b.eintraege.map(renderRow).join("")}</tbody>
      </table>
    </section>`;
}

function renderTable(el: HTMLElement): void {
  const table = el.querySelector<HTMLElement>("#aw-table")!;
  if (!report) return;
  if (report.stellen.length === 0) {
    table.innerHTML = `<div class="aw-empty">Es gibt noch keine Wünsche.</div>`;
    return;
  }
  const blocks = visibleBlocks(report);
  table.innerHTML = blocks.length === 0
    ? `<div class="aw-empty">Für die Auswahl gibt es keine Wünsche.</div>`
    : blocks.map(renderBlock).join("");
}

function openInfo(): void {
  openModal("Wie diese Auswertung zu lesen ist", `
    <p class="aw-info-lead">Je Praktikumsstelle und Fachbereich stehen die Schüler, die sich die Stelle gewünscht haben, in der Reihenfolge, in der sie sie eingetragen haben.</p>
    <table class="aw-info-table">
      <tr><td><strong>Rang</strong></td><td>Reihenfolge des Eintragens an dieser Stelle in diesem Fachbereich, über alle Schüler der Schule. Die Filter blenden nur Zeilen aus, der Rang bleibt stehen.</td></tr>
      <tr><td><strong>Wunsch</strong></td><td>Position auf der Wunschliste des Schülers. Nr. 1 ist sein liebster Wunsch.</td></tr>
      <tr><td><strong>Eingetragen am</strong></td><td>Zeitpunkt, an dem der Schüler die Stelle zum ersten Mal auf seine Liste gesetzt hat, auf die Sekunde genau.</td></tr>
      <tr><td><span class="badge badge-amber">ungenau</span></td><td>Der Wunsch stammt aus der Zeit vor der sekundengenauen Erfassung. Angezeigt wird das letzte Speichern der Wunschliste, der Wunsch kann also früher entstanden sein. Er wird nach diesem Zeitpunkt eingereiht.</td></tr>
    </table>

    <h3 class="aw-info-head">Zählregeln</h3>
    <ul class="aw-info-list">
      <li>Sortiert ein Schüler seine Liste um, behält jeder Wunsch seinen Zeitpunkt.</li>
      <li>Entfernt er eine Stelle und fügt sie wieder hinzu, gilt der neue Zeitpunkt.</li>
      <li>Wünsche inaktiver Schüler und an inaktive Praktikumsstellen sind nicht enthalten.</li>
    </ul>`);
}

function exportData(fmt: "xlsx" | "pdf"): void {
  if (!report) return;
  const stellen = visibleBlocks(report);
  if (stellen.length === 0) { showToast("Es gibt nichts zu exportieren."); return; }
  void exportWuensche(fmt, { stellen, filter: filterText(report) })
    .catch((err: any) => showToast(`Export fehlgeschlagen: ${err.message}`));
}

async function loadReport(el: HTMLElement): Promise<void> {
  const table = el.querySelector<HTMLElement>("#aw-table")!;
  table.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;
  try {
    report = await api.auswertungen.wuensche();
  } catch (err: any) {
    report = null;
    table.innerHTML = `<div class="aw-empty">Die Auswertung konnte nicht geladen werden: ${esc(err.message)}</div>`;
    return;
  }
  fillFilters(el, report);
  renderTable(el);
}

// ── Seite ──

export async function render(el: HTMLElement): Promise<void> {
  (el as any).__renderAC?.abort();
  const ac = new AbortController();
  (el as any).__renderAC = ac;

  el.innerHTML = `
    <div class="page-header"><h1>Wünsche pro Praktikumsstelle</h1></div>
    <div class="aw-wrap">
      <div class="aw-controls">
        <div class="dropdown-check" id="ww-dept">
          <button type="button" class="dropdown-check-btn">Alle Fachbereiche</button>
          <div class="dropdown-check-panel dropdown-check-panel-left"></div>
        </div>
        <div class="dropdown-check" id="ww-class">
          <button type="button" class="dropdown-check-btn">Alle Klassen</button>
          <div class="dropdown-check-panel dropdown-check-panel-left"></div>
        </div>
        <button type="button" class="btn btn-secondary btn-square" id="aw-info"
                title="Wie diese Auswertung zu lesen ist" aria-label="Wie diese Auswertung zu lesen ist">${Icons.informationCircle}</button>
        <div class="aw-spacer"></div>
        <div class="dropdown-check" id="aw-download">
          <button type="button" class="btn btn-secondary dropdown-check-btn">Download</button>
          <div class="dropdown-check-panel">
            <div class="dropdown-check-item" data-fmt="xlsx">Als Excel</div>
            <div class="dropdown-check-item" data-fmt="pdf">Als PDF</div>
          </div>
        </div>
      </div>
      <div class="aw-table-wrap" id="aw-table"></div>
    </div>`;

  deptDropdown = initDropdownCheck(el.querySelector<HTMLElement>("#ww-dept")!, values => {
    selectedDepts = values;
    renderTable(el);
  }, { allSelectedShowsDefault: true });
  classDropdown = initDropdownCheck(el.querySelector<HTMLElement>("#ww-class")!, values => {
    selectedClasses = values;
    renderTable(el);
  }, { allSelectedShowsDefault: true });

  el.querySelector("#aw-info")!.addEventListener("click", openInfo);

  const dd = el.querySelector<HTMLElement>("#aw-download")!;
  dd.querySelector(".dropdown-check-btn")!.addEventListener("click", () => dd.classList.toggle("open"));
  dd.querySelector(".dropdown-check-panel")!.addEventListener("click", e => {
    const item = (e.target as HTMLElement).closest(".dropdown-check-item") as HTMLElement | null;
    if (!item) return;
    dd.classList.remove("open");
    exportData(item.dataset.fmt as "xlsx" | "pdf");
  });
  // Filter und Download schließen bei einem Klick daneben.
  document.addEventListener("click", e => {
    el.querySelectorAll<HTMLElement>(".dropdown-check.open").forEach(d => {
      if (!d.contains(e.target as Node)) d.classList.remove("open");
    });
  }, { signal: ac.signal });

  fitOnResize(el.querySelector<HTMLElement>("#aw-table")!, ac.signal);
  await loadReport(el);
}
